import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { MatDivider } from '@angular/material/divider';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { CurrencyPipe, DatePipe, NgForOf, NgIf } from '@angular/common';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatTooltip } from '@angular/material/tooltip';
import { ShoppingCartItem } from '../shopping-cart.types';
import { OpenQuoteRequestsItem } from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/quote-requests.types';

interface CartMenuItem {
  label: string;
  icon?: string;
  enabled: boolean;
  action?: () => void;
}

@Component({
  selector: 'cipx-shopping-cart-item',
  templateUrl: './shopping-cart-item.component.html',
  imports: [
    MatDivider,
    ReactiveFormsModule,
    MatExpansionPanelHeader,
    MatExpansionPanel,
    MatAccordion,
    CurrencyPipe,
    DatePipe,
    NgForOf,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatIconButton,
    NgIf,
    MatCard,
    MatCardContent,
    MatTooltip,
  ],
  standalone: true,
})
export class CIPXShoppingCartItemComponent implements OnInit {
  @Input() item!: ShoppingCartItem<OpenQuoteRequestsItem>;
  @Input() toggleAccordion: boolean = false;
  @Input() useAccordionComponent: boolean = true;
  @Input() fullDisplayWithoutAccordion: boolean = false;
  @Output() removeItem = new EventEmitter<ShoppingCartItem<OpenQuoteRequestsItem>>();
  @Output() updateQuantity = new EventEmitter<{ item: ShoppingCartItem<OpenQuoteRequestsItem>, quantity: number }>();
  @Output() viewDetails = new EventEmitter<ShoppingCartItem<OpenQuoteRequestsItem>>();

  readonly panelOpenState = signal(false);

  _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  _changeDetector = inject(ChangeDetectorRef);

  isScreenSmall: boolean = false;
  _unsubscribeAll: Subject<void> = new Subject<void>();
  menuItems: CartMenuItem[] = [];

  ngOnInit() {
    this.menuItems = this.createMenuItems();

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });
  }

  private createMenuItems(): CartMenuItem[] {
    return [
      {
        label: 'View Details',
        icon: 'visibility',
        enabled: true,
        action: () => this.onViewDetails()
      },
      {
        label: 'Remove from Cart',
        icon: 'delete',
        enabled: true,
        action: () => this.onRemoveItem()
      }
    ];
  }

  onMenuItemClick(menuItem: CartMenuItem) {
    if (!menuItem.enabled) {
      return;
    }

    if (typeof menuItem.action === 'function') {
      menuItem.action();
    }
  }

  onRemoveItem() {
    this.removeItem.emit(this.item);
  }

  onViewDetails() {
    this.viewDetails.emit(this.item);
  }

  onQuantityChange(newQuantity: number) {
    if (newQuantity > 0) {
      this.updateQuantity.emit({ item: this.item, quantity: newQuantity });
    }
  }

  getDurationTerm(): string {
    const data = this.item.data;
    if (data?.selected_quote) {
      return `${data.selected_quote.duration}-${data.selected_quote.duration_units} Term`;
    }
    return 'Term not specified';
  }

  getFormattedAddress(): string {
    const address = this.item.data?.principal_address;
    if (!address) return 'Address not available';

    return `${address.street_1}${address.street_2 ? ', ' + address.street_2 : ''}, ${address.city}, ${address.state_abbreviation} ${address.zip}`;
  }

  getCompanyName(): string {
    return this.item.data?.quote_request_bond?.company_name || 'Company name not available';
  }

  getCompanyDBA(): string {
    return this.item.data?.company_dba || '';
  }

  getStateAndBondLabel(): string {
    const data = this.item.data;
    const state = data?.quote_request_bond?.state_abbreviation || '';
    const bondLabel = data?.quote_request_bond?.surety_bond_label || '';
    return `${state} - ${bondLabel}`;
  }

  getEffectiveDate(): string {
    return this.item.data?.selected_quote?.bond_effective_date || '';
  }

  getPremium(): number {
    return this.item.data?.selected_quote?.premium || 0;
  }

  getOrderNumber(): string {
    return this.item.data?.order_number || this.item.display_name;
  }

  getQuoteRequestDate(): Date | string | null {
    return this.item.data?.quote_request_date || null;
  }

  getCompanyType(): string {
    return this.item.data?.quote_request_bond?.company_type_label || '';
  }

  getOwnerName(): string {
    const data = this.item.data;
    return `${data?.user_first_name || ''} ${data?.user_last_name || ''}`.trim() || 'Owner not specified';
  }

  getOwnerPhone(): string {
    return this.item.data?.user_phone || '';
  }

  getShippingOption(): string {
    return this.item.data?.shipping_details?.shipping_option_label || 'Standard delivery';
  }

  hasAttachedFiles(): boolean {
    return (this.item.data?.quote_request_bond?.related_files?.length ?? 0) > 0;
  }

  getAttachedFiles(): any[] {
    return this.item.data?.quote_request_bond?.related_files || [];
  }

  canPurchase(): boolean {
    return this.item.data?.customer_can_purchase === 1;
  }

  isPurchased(): boolean {
    return this.item.data?.quote_status_stage === 'Issued';
  }

  protected readonly isNaN = isNaN;
}
